import React, { createContext, useContext, useEffect, useState } from "react";
import countryToCurrency from "country-to-currency";
import { getExampleNumber } from "libphonenumber-js";
import examples from "libphonenumber-js/mobile/examples";

const SUPPORTED_CURRENCIES = new Set(["EUR", "GBP", "USD", "INR"]);

const FALLBACK = {
  country_code: "DE",
  default_city: "Berlin",
  currency: "EUR",
  phone_placeholder: "+49 1512 3456789",
  loading: true,
};

const LocaleCtx = createContext(FALLBACK);

export function useLocale() {
  return useContext(LocaleCtx);
}

function phonePlaceholderFor(countryCode) {
  try {
    const n = getExampleNumber(countryCode, examples);
    return n ? n.formatInternational() : FALLBACK.phone_placeholder;
  } catch {
    return FALLBACK.phone_placeholder;
  }
}

function currencyForCountry(countryCode) {
  const native = countryToCurrency[countryCode];
  if (SUPPORTED_CURRENCIES.has(native)) return native;
  return FALLBACK.currency;
}

function resolveLocale({ countryCode, city } = {}) {
  const cc = (countryCode || FALLBACK.country_code).toUpperCase();
  return {
    country_code: cc,
    default_city: city || FALLBACK.default_city,
    currency: currencyForCountry(cc),
    phone_placeholder: phonePlaceholderFor(cc),
  };
}

/** IP geo — country for currency/phone bias; city for form defaults. */
async function detectGeoInBrowser() {
  try {
    const res = await fetch("https://ipwho.is/", {
      headers: { Accept: "application/json" },
      signal: AbortSignal.timeout(5000),
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (!data?.success || !data?.country_code) return null;
    return {
      countryCode: String(data.country_code).toUpperCase(),
      city: data.city ? String(data.city) : null,
    };
  } catch {
    return null;
  }
}

export function LocaleProvider({ children }) {
  const [locale, setLocale] = useState(FALLBACK);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const geo = await detectGeoInBrowser();
        if (cancelled) return;
        setLocale({ ...resolveLocale(geo || {}), loading: false });
      } catch {
        if (!cancelled) setLocale((l) => ({ ...l, loading: false }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return <LocaleCtx.Provider value={locale}>{children}</LocaleCtx.Provider>;
}
